/**
 * Tags the platform writes into a product's `productTags` itself. None of them
 * is ever the seller's.
 *
 * "discounted" is the only one today. createDiscount and sweepDiscounts
 * (functions/src/index.ts) append it while a sale is live and strip it when the
 * sale ends, so it is a price state stored inside a list the seller writes. Two
 * rules follow, and every reader of `productTags` keeps them:
 *
 *  - IT IS NOT A TOPIC. The public catalog (catalog-tags.ts), the seller's tag
 *    picker and the tag counters behind the picker all leave it out. The picker
 *    used to offer it, numbered by how many products were on sale.
 *  - IT IS NOT EVIDENCE OF A SALE. A seller can write the word into their own
 *    list (the picker made that one click), and the stored marker trails a
 *    window that has just opened or closed by up to a minute. So the browse
 *    filters and facets ignore the stored marker and decide "discounted" from
 *    the price the card shows: see filterTagsOf in product-filters.ts.
 *
 * MIRRORED in functions/src/lib/platform-tags.ts, which keeps the counters and
 * the owners' "product updated" notices off the marker, and which the importer
 * uses to drop it from imported tags. platform-tags.test.ts reads that file and
 * fails if the two lists differ.
 */

/** The marker the discount engine adds while a sale is live. */
export const SALE_TAG = 'discounted';

/** Every platform tag, in the reduced form that platformTagKey produces. */
export const PLATFORM_TAGS: readonly string[] = [SALE_TAG];

/**
 * A tag reduced to the form that decides whether it is a platform tag: trimmed,
 * lowercased, and every run of other characters made one hyphen. The same
 * reduction as tagSlug in catalog-tags.ts (not imported from there, because
 * catalog-tags.ts reaches this module through product-filters.ts), so
 * "Discounted" written straight to Firestore is caught like the marker itself.
 */
function platformTagKey(tag: string): string {
  return tag.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Whether a stored tag is one the platform owns, in any spelling. */
export function isPlatformTag(tag: unknown): boolean {
  return typeof tag === 'string' && PLATFORM_TAGS.includes(platformTagKey(tag));
}

/** A tag list without its platform tags: the seller's own, in their order. */
export function withoutPlatformTags<T>(tags: readonly T[]): T[] {
  return tags.filter((tag) => !isPlatformTag(tag));
}
